import {
  SessionStatus,
  environmentLabel,
  isTradableEnvironment,
  useTrading,
  type ConnectionView,
} from "@helm/core";

const SESSION_WORDS: Partial<Record<SessionStatus, string>> = {
  [SessionStatus.DISCONNECTED]: "disconnected",
  [SessionStatus.CONNECTING]: "connecting",
  [SessionStatus.SYNCING]: "syncing",
  [SessionStatus.READY]: "ready",
  [SessionStatus.DEGRADED]: "degraded — reconnecting",
  [SessionStatus.KICKED]: "logged out by Rithmic",
};

/**
 * Gateway connection and Rithmic session state at the top of every mobile tab (CB5): the owner
 * must see at a glance, on the phone too, whether what TradePrizm shows is live. The desktop shows
 * the same facts in the status line's connection item (topbar/ConnectionItem.tsx) instead. Anything but a live gateway and a ready order plant is a warning; while the
 * gateway is not connected, positions and P&L on screen are the last known values, not live.
 */
export function ConnectionBanner({ connection }: { connection: ConnectionView }): React.JSX.Element {
  const session = useTrading((s) => s.session);
  if (connection.status === "connected") {
    const ready = session?.status === SessionStatus.READY;
    return (
      <div
        className={session === null || ready ? "banner banner-info" : "banner banner-warn"}
        role="status"
        data-connection="connected"
        data-session={session === null ? "none" : ready ? "ready" : "not-ready"}
      >
        {session === null
          ? "Gateway connected — no Rithmic login yet. Trading is disabled."
          : ready && isTradableEnvironment(session.environment)
            ? `Rithmic order plant ready — SIMULATED trading on ${environmentLabel(session.environment) === "mock" ? "the local mock" : "Rithmic Test"}.`
            : ready && isTradableEnvironment(session.environment, session.tradingEnabled)
              ? `Rithmic order plant ready — ${environmentLabel(session.environment)} account ${session.systemName ? `(${session.systemName}) ` : ""}with "Allow trading" ON: orders are real.`
              : ready
                ? `Rithmic order plant ready — ${environmentLabel(session.environment)}: "Allow trading" is off for this login; new orders are disabled, exits and cancels work.`
                : `Rithmic order plant ${SESSION_WORDS[session.status] ?? "not ready"}${session.detail ? ` — ${session.detail}` : ""}. Trading is disabled.`}
      </div>
    );
  }
  const attempt =
    connection.reconnectAttempt > 0 ? ` (attempt ${String(connection.reconnectAttempt + 1)})` : "";
  // Before the first Welcome there is nothing on screen that could be mistaken for live.
  const hadData = connection.sessionId !== null;
  return (
    <div className="banner banner-warn" role="status" data-connection={connection.status}>
      {connection.status === "connecting"
        ? hadData
          ? "Reconnecting to gateway…"
          : "Connecting to gateway…"
        : "Disconnected from gateway"}
      {attempt}
      {connection.lastError ? ` — ${connection.lastError}` : ""}
      {hadData && ". Positions, orders and P&L shown are the last known values, not live."}
    </div>
  );
}
